import { Link } from 'react-router';
import { X } from 'lucide-react';
import { useAuth } from '@/auth/store';
import { useDismissed } from '@/lib/useDismissed';
import { usePlan } from '@/plan/store';
import { daysUntil } from '@/plan/trial';
import { useTeam } from '@/team/store';

/**
 * `dismissId` marks a notice the person may close: only a trial running out, which the header's badge already counts
 * down (TW-14). The id carries the trial's end, so a later trial asks again. What changes what the person can do
 * (a failed payment, paused sync, a read-only team) cannot be closed.
 */
type Message = { tone: string; text: string; cta: string; to: string; dismissId?: string } | null;

/** The team's plan first (an owner's problem), then the person's own. Never both. */
function teamMessage(membership: ReturnType<typeof useTeam.getState>['membership']): Message {
  if (!membership) return null;
  const { team, role } = membership;
  const daysLeft = team.trialEndsAt ? daysUntil(team.trialEndsAt) : null;
  const to = role === 'owner' ? '/team/billing' : '/team';
  if (team.planStatus === 'past_due') return { tone: 'bg-danger-bg text-danger-fg', text: 'A payment failed. The team keeps working for now; update the card to avoid interruption.', cta: 'Fix billing', to };
  // Paused with no trial end: a second team, which starts without one (TW-16). No trial ever ended for it.
  if (team.planStatus === 'paused') return { tone: 'bg-warning-bg text-warning-fg', text: team.trialEndsAt ? 'The trial ended, so the team is read-only. Nothing is lost - add a card to carry on.' : 'The team is read-only until a card is added. The 7-day trial went to your earlier team.', cta: 'Add a card', to };
  if (team.planStatus === 'trialing' && daysLeft !== null && daysLeft <= 3) return { tone: 'bg-notice-bg text-notice-fg', text: daysLeft <= 0 ? 'Your team trial ends today.' : `Your team trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}. Add a card any time; nothing is charged until it ends.`, cta: 'Add a card', to, dismissId: `trial:team:${team.id}:${team.trialEndsAt}` };
  return null;
}

/** Only when it changes what the person can do. A live plan says nothing; a lapsed one says where the data is. */
function personalMessage(summary: ReturnType<typeof usePlan.getState>['summary']): Message {
  if (!summary || summary.via === 'team') return null;
  const to = '/settings/account';
  if (!summary.entitled) return { tone: 'bg-notice-bg text-notice-fg', text: 'Sync is paused. Everything is safe on this device; upgrade to Personal to sync it again.', cta: 'See plans', to };
  if (summary.status === 'past_due') return { tone: 'bg-danger-bg text-danger-fg', text: 'A payment failed. Sync keeps working for a few days; update the card to avoid a gap.', cta: 'Fix billing', to };
  if (summary.source === 'trial' && summary.currentPeriodEnd) {
    const daysLeft = daysUntil(summary.currentPeriodEnd);
    if (daysLeft <= 3) return { tone: 'bg-notice-bg text-notice-fg', text: daysLeft <= 0 ? 'Your free 7 days end today.' : `Your free 7 days end in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}. Upgrade to keep syncing; your data stays either way.`, cta: 'Upgrade', to, dismissId: `trial:personal:${summary.currentPeriodEnd}` };
  }
  return null;
}

/** One quiet line when a plan needs attention: trial ending, payment failed, or sync paused. */
export function PlanBanner() {
  const signedIn = useAuth((s) => s.status === 'signedIn');
  const membership = useTeam((s) => s.membership);
  const summary = usePlan((s) => s.summary);
  const { isDismissed, dismiss } = useDismissed();
  const message = signedIn ? teamMessage(membership) ?? personalMessage(summary) : null;
  if (!message || (message.dismissId && isDismissed(message.dismissId))) return null;
  const { dismissId } = message;
  return (
    <div className={`relative flex items-center justify-center gap-3 px-10 py-2 text-body-sm ${message.tone}`} role="status">
      <span>{message.text}</span>
      <Link to={message.to} className="font-bold underline text-inherit">{message.cta}</Link>
      {dismissId && (
        <button type="button" aria-label="Dismiss the trial reminder" onClick={() => dismiss(dismissId)}
          className="absolute right-2 top-1/2 -translate-y-1/2 size-7 grid place-items-center rounded-full border-0 bg-transparent text-inherit opacity-70 cursor-pointer hover:opacity-100">
          <X size={15} />
        </button>
      )}
    </div>
  );
}
